import classnames from 'classnames';
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CivilDate } from 'tc39-proposal-temporal';
import { SingleMonthCalendar } from '@bingads-webui-react/single-month-calendar';

import { civilDateRangeEqual } from '../../../utils/date-utils';

// this is a simple date range picker based on the Calendar control used in
// Campaign UI (NOT Fluent UI Calendar)
// the 'Compare' feature is not supported, so only one date range selection
// is supported
const SimpleDateRangePickerInternal = ({
  defaultSelectedDateRange,
  // to select a date range, need to select 2 days (start & end) on calendars
  // this value control which value is currently selecting
  defaultIsSelectingStartDate,
  onDateRangeChange,
  timeZone,
  i18n,
}) => {
  // use ref instead of state to avoid unnecessary rending
  const isSelectingStartDate = useRef(defaultIsSelectingStartDate);

  const [selectedDateRange, setSelectedDateRange]
    = useState(defaultSelectedDateRange);

  // the navigatedDate set on the left calendar, which control which month
  // is shown on the calendar
  const [leftNavigatedDate, setLeftNavigationDate] = useState(new CivilDate(
    defaultSelectedDateRange.end.year,
    defaultSelectedDateRange.end.month - 1,
    15
  ));

  // the navigatedDate set on the right calendar
  const rightNavigatedDate = useMemo(
    () => new CivilDate(leftNavigatedDate.year, leftNavigatedDate.month + 1, 15),
    [leftNavigatedDate]
  );

  const today = useMemo(() => {
    const todayObj = i18n.getToday({ timeZone });
    return new CivilDate(todayObj.year, todayObj.month, todayObj.day);
  }, [i18n, timeZone]);

  // for now, use the default values here
  const minDate = useMemo(() => today.plus({ years: -3 }), [today]);
  const maxDate = today;

  // 'Compare' is not supported, so only one range is used
  const selectedRanges = useMemo(() => [selectedDateRange], [selectedDateRange]);

  // click '<' button on the left calendar
  const onNavigateStartMonthDate = useCallback((date) => {
    setLeftNavigationDate(date);
  }, []);

  // click '>' button on the right calendar
  const onNavigateEndMonthDate = useCallback((date) => {
    setLeftNavigationDate(date.plus({ months: -1 }));
  }, []);

  const onSelectDate = useCallback((newDate) => {
    let { start, end } = selectedDateRange || {};
    if (isSelectingStartDate.current) {
      start = newDate;
      end = end > newDate ? end : newDate;
    } else {
      end = newDate;
    }
    if (start > end) {
      setSelectedDateRange({ start: end, end: start });
    } else {
      setSelectedDateRange({ start, end });
    }
    isSelectingStartDate.current = !isSelectingStartDate.current;
  }, [selectedDateRange]);

  useEffect(() => {
    if (!civilDateRangeEqual(selectedDateRange, defaultSelectedDateRange) && onDateRangeChange) {
      onDateRangeChange(selectedDateRange);
    }
  }, [onDateRangeChange, selectedDateRange, defaultSelectedDateRange]);

  return (
    <div className={classnames('simple-date-range-picker')}>
      <div className="compared-date-range-picker-dialog">
        <div className="double-month-calendar">
          <div className="left-calendar">
            <SingleMonthCalendar
              minDate={minDate}
              maxDate={maxDate}
              today={today}
              onSelectDate={onSelectDate}
              selectedRanges={selectedRanges}
              navigatedDate={leftNavigatedDate}
              onNavigateDate={onNavigateStartMonthDate}
              isRightNavigateVisible={false}
              i18n={i18n}
            />
          </div>
          <div className="divider" />
          <div className="right-calendar">
            <SingleMonthCalendar
              minDate={minDate}
              maxDate={maxDate}
              today={today}
              onSelectDate={onSelectDate}
              selectedRanges={selectedRanges}
              navigatedDate={rightNavigatedDate}
              onNavigateDate={onNavigateEndMonthDate}
              isLeftNavigateVisible={false}
              i18n={i18n}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

SimpleDateRangePickerInternal.propTypes = {
  defaultSelectedDateRange: PropTypes.shape({
    start: PropTypes.instanceOf(CivilDate),
    end: PropTypes.instanceOf(CivilDate),
  }).isRequired,
  defaultIsSelectingStartDate: PropTypes.bool,
  onDateRangeChange: PropTypes.func,
  timeZone: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    getToday: PropTypes.func,
  }).isRequired,
};

SimpleDateRangePickerInternal.defaultProps = {
  defaultIsSelectingStartDate: true,
  onDateRangeChange: undefined,
};

export const SimpleDateRangePicker = SimpleDateRangePickerInternal;
